import { FC, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNil } from 'lodash';
// Import Types
import type { ABPreset, CampaignPreviewForCampaignReport } from 'types';
// Import Store
import { useAppSelector } from 'store';
import { selectUserCurrencyRate } from 'store/slices/currencyRates';
import { selectUserSettingsCurrencyId } from 'store/slices/userData';
// Import Hooks
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Services
import { ABTestingHttpService, CampaignHttpService } from 'services/http';
// Import UI Components
import { Alert, BusyLoader, Empty, Tab, Tabs } from '@geneui/components';
// Import Components
import ABTestGeneralDetails from './ABTestGeneralDetails';
import ABTestChartGroup from './ABTestChartGroup';
// Import Types
import { TestResultsAPIModel } from './types';
// Import Constants
import { l } from 'constants/common';
import { parseToUIModel } from './config';

interface Props {
    campaignId: string;
    campaignData?: Nullable<CampaignPreviewForCampaignReport>;
}

const CampaignReportABTest: FC<Props> = ({ campaignId, campaignData }) => {
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();
    const { partnerDateFormat } = usePartnerDateFormats();

    const currencyId = useAppSelector(selectUserSettingsCurrencyId);
    const rate = useAppSelector(selectUserCurrencyRate);

    const [testResultsAPIModel, setTestResultsAPIModel] = useState<TestResultsAPIModel[] | null>(null);
    const [promotionPresetsAPIModel, setPromotionPresetsAPIModel] = useState<ABPreset[] | null>(null);

    const isLoading: boolean = isNil(testResultsAPIModel) || isNil(promotionPresetsAPIModel);

    const { getCampaignABTestResult, getPresets } = useCancelableRequests({
        getCampaignABTestResult: CampaignHttpService.getCampaignABTestResult,
        getPresets: ABTestingHttpService.getPresets,
    });

    const aBTestResultsUIModel = useMemo(() => {
        if (!isNil(testResultsAPIModel) && !isNil(promotionPresetsAPIModel)) {
            return parseToUIModel({
                testResultsAPI: testResultsAPIModel,
                promotionPresets: promotionPresetsAPIModel,
                t,
                partnerDateFormat,
                campaignNextRunDate: campaignData?.NextRunDate,
                currencyId,
                rate,
            });
        }
    }, [
        testResultsAPIModel,
        promotionPresetsAPIModel,
        t,
        campaignData?.NextRunDate,
        currencyId,
        rate,
        partnerDateFormat,
    ]);

    const init = () => {
        doGetRequest(getCampaignABTestResult.request, {
            queryString: { CampaignId: campaignId },
            successCallback: (data: TestResultsAPIModel[]) => setTestResultsAPIModel(data),
        });

        doGetRequest(getPresets.request, {
            successCallback: (data) => setPromotionPresetsAPIModel(data),
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <div className="campaign-report-ab-test-tab-container">
            <BusyLoader isBusy={isLoading || isNil(aBTestResultsUIModel)} type="spinner" spinnerSize="medium">
                {isEmpty(aBTestResultsUIModel) ? (
                    <Empty title={t(l.NoDataToDisplay)} />
                ) : (
                    <Tabs type="box" position="top">
                        {aBTestResultsUIModel?.map(
                            ({ tabTitle, generalDetails, kpiList, isResultEmpty }, index: number) => {
                                return (
                                    <Tab key={index} title={tabTitle}>
                                        <div className="campaign-report-ab-test-scrollable-container">
                                            <ABTestGeneralDetails generalDetails={generalDetails} />
                                            {isResultEmpty && (
                                                <Alert
                                                    closable={false}
                                                    title={t(l.ABTestNoResultYet)}
                                                    type="warning"
                                                    iconProps={{ type: 'bc-icon-info' }}
                                                />
                                            )}
                                            <ABTestChartGroup kpiList={kpiList} />
                                        </div>
                                    </Tab>
                                );
                            },
                        )}
                    </Tabs>
                )}
            </BusyLoader>
        </div>
    );
};

export default memo(CampaignReportABTest);
